import { FC, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setProgressStatus } from '@/store/slices/progressSlice';
import { useAppSelector } from '@/store/store';
import { StorageFlow } from '@/types/common';
import { getRouteDataByUrl } from '@/routes/Routes.helpers';
import { useRouteProperties } from '@/hooks/useRouteProperties';
import { getLocalStorageItem, setStorageItem } from '@/helpers/storage';
import GlobalLoading from '@/components/elems/GlobalLoading/GlobalLoading.component';
import ScrollToTopButton from '@/components/elems/ScrollToTopButton/ScrollToTopButton.component';
import { PAGES_WITH_SCROLL_BUTTON } from '@/components/elems/ScrollToTopButton/ScrollToTopButton.config';
import { DEFAULT_PADDING } from '@/components/layouts/SiteLayout/SiteLayout.config';
import styles from '@/components/layouts/SiteLayout/SiteLayout.module.scss';
import { LoginedWrapperPropsIF } from '@/components/layouts/SiteLayout/SiteLayout.types';
import AddPhotoWidget from '@/components/widgets/AddPhotoWidget/AddPhotoWidget.component';
import HeaderWidget from '@/components/widgets/HeaderWidget/HeaderWidget.component';
import ProgressWidget from '@/components/widgets/ProgressWidget/ProgressWidget.component';
import { TabBarWidget } from '@/components/widgets/TabBarWidget/TabBarWidget.component';
import { TASK_STATUSES } from '@/constants/statuses';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const SiteLayout: FC<LoginedWrapperPropsIF> = ({ children }) => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const { isHideHeader, isShowTabBar, isPrivateRoute } = useRouteProperties();
    const topBlockRef = useRef<HTMLDivElement | null>(null);
    const layoutRef = useRef<HTMLDivElement | null>(null);

    const { isGlobalLoading } = useAppSelector((state) => state.siteConfig);
    const { progressStatus } = useAppSelector((state) => state.progressSlice);

    const isShowScrollButton = PAGES_WITH_SCROLL_BUTTON.includes(
        getRouteDataByUrl(pathname)?.path || '',
    );

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        if (!isPrivateRoute) {
            layoutRef.current?.style.removeProperty('padding-top');
            return;
        }

        if (!layoutRef?.current) {
            return;
        }

        if (!topBlockRef?.current || isHideHeader) {
            layoutRef.current.style.paddingTop = `${DEFAULT_PADDING}px`;
            return;
        }

        layoutRef.current.style.paddingTop = `${topBlockRef.current.offsetHeight + DEFAULT_PADDING}px`;
    }, [isHideHeader, progressStatus]);

    useEffect(() => {
        setStorageItem<StorageFlow>(STORAGE_KEYS.Flow, null);

        if (getLocalStorageItem(STORAGE_KEYS.ClothingForm)) {
            dispatch(setProgressStatus(TASK_STATUSES.WaitingUser));
        }
    }, []);

    return (
        <div
            className={`
        ${isPrivateRoute ? styles.siteLayoutWrapper : ''}
        ${isShowTabBar ? styles.isShowTabBar : ''}
      `}
            ref={layoutRef}
        >
            {!isHideHeader && (
                <div
                    className={`flcol ${styles.topFixedBlock}`}
                    ref={topBlockRef}
                >
                    <HeaderWidget />
                    <ProgressWidget className={styles.progressWidget} />
                    {isShowScrollButton && <ScrollToTopButton />}
                </div>
            )}
            <main className={styles.siteMainWrapper}>{children}</main>
            <AddPhotoWidget />
            {isShowTabBar && <TabBarWidget />}
            {isGlobalLoading && <GlobalLoading />}
        </div>
    );
};

export default SiteLayout;
